import { ENV } from "../../util/helpers";
import { Comparison, Field, File, FilterField } from "../data-types";
import { MetadataCollection } from "../data-types";

export class UrlApiQuery {
  public static returnContainersRequest = (context?: string): string => {
    let endpointUrl = new URL(`${ENV.apiUrl}/api/v1/containers`);
    if (context) {
      endpointUrl.searchParams.append("context", context);
    }
    return endpointUrl.toString();
  };

  public static returnContainerContentsRequest = (
    containerName: string,
    folder?: string,
    continuationToken?: string,
    limit?: number
  ): string => {
    let endpointUrl = new URL(
      `${ENV.apiUrl}/api/v1/container/${encodeURIComponent(
        containerName
      )}/contents`
    );
    if (folder) {
      endpointUrl.searchParams.append("folder", folder);
    }
    if (continuationToken) {
      endpointUrl.searchParams.append("ContinuationToken", continuationToken);
    }
    if (limit) {
      endpointUrl.searchParams.append("Limit", limit.toString());
    }
    return endpointUrl.toString();
  };

  public static returnDownloadFileRequest = (
    containerName: string,
    fileName: string,
    fileGUID: string,
    folder?: string
  ): string => {
    const endpointURL = new URL(
      `${ENV.apiUrl}/api/v1/download/${encodeURIComponent(containerName)}/file`
    );
    endpointURL.searchParams.append("fileName", fileName);
    if (folder) {
      endpointURL.searchParams.append("folder", folder ?? "");
    }
    endpointURL.searchParams.append("fileGuid", fileGUID);
    return endpointURL.toString();
  };

  public static returnFolderSearchQuery = (
    containerName: string,
    offset: number,
    limit: number,
    folder?: string,
    fileName?: string,
    sortField?: Field,
    ascending?: boolean
  ): string => {
    const endpointURL = new URL(
      `${ENV.apiUrl}/api/v1/search/files/${encodeURIComponent(
        containerName
      )}/fileOrFolder`
    );
    if (folder) {
      endpointURL.searchParams.append("folder", folder);
    }
    if (fileName) {
      endpointURL.searchParams.append("fileName", fileName);
    }
    if (sortField) {
      endpointURL.searchParams.append("sortField", Field[sortField]);
    }
    if (ascending !== undefined) {
      endpointURL.searchParams.append("ascending", ascending.toString());
    }
    endpointURL.searchParams.append("Offset", offset.toString());
    endpointURL.searchParams.append("Limit", limit.toString());
    return endpointURL.toString();
  };

  public static returnMetadataSearchQuery = (
    containerName: string,
    metaDataKey: string,
    metaDataValue: string,
    offset: number,
    limit: number,
    folder?: string,
    sortField?: Field,
    ascending?: boolean
  ): string => {
    const endpointURL = new URL(
      `${ENV.apiUrl}/api/v1/search/files/${encodeURIComponent(
        containerName
      )}/metaDataKey/${encodeURIComponent(
        metaDataKey
      )}/metaDataValue/${encodeURIComponent(metaDataValue)}`
    );
    if (folder) {
      endpointURL.search = decodeURIComponent(
        new URLSearchParams([
          ["folder", folder],
          ["Offset", offset.toString()],
          ["Limit", limit.toString()],
        ]).toString()
      );
    } else {
      endpointURL.search = new URLSearchParams([
        ["Offset", offset.toString()],
        ["Limit", limit.toString()],
      ]).toString();
    }
    if (sortField) {
      endpointURL.searchParams.append("sortField", sortField.toString());
    }
    if (ascending !== undefined) {
      endpointURL.searchParams.append("ascending", ascending.toString());
    }
    return endpointURL.toString();
  };

  public static returnGdriveSearchQuery = (
    containerName: string,
    gdriveBucket: string,
    gdrivePath: string,
    offset: number,
    limit: number
  ): string => {
    const endpointURL = new URL(
      `${ENV.apiUrl}/api/v1/search/files/${encodeURIComponent(
        containerName
      )}/OldContainerName/${encodeURIComponent(
        gdriveBucket
      )}/filepath/${encodeURIComponent(gdrivePath)}`
    );
    endpointURL.search = new URLSearchParams([
      ["Offset", offset.toString()],
      ["Limit", limit.toString()],
    ]).toString();
    return endpointURL.toString();
  };

  public static returnUploadFileOneQuery = (
    containerName: string,
    folder?: string,
    fileName?: string
  ): string => {
    const endpointURL = new URL(
      `${ENV.apiUrl}/api/v1/upload/${encodeURIComponent(containerName)}/fileObj`
    );
    if (folder) {
      endpointURL.searchParams.append("folder", folder);
    }
    if (fileName) {
      endpointURL.searchParams.append("fileName", fileName);
    }
    return endpointURL.toString();
  };

  public static returnUploadPartsInitQuery = (
    containerName: string,
    fileName: string,
    folder?: string
  ): string => {
    const endpointURL = new URL(
      `${ENV.apiUrl}/api/v1/uploadPartsInit/${encodeURIComponent(
        containerName
      )}/file/${encodeURIComponent(fileName)}`
    );
    if (folder) {
      endpointURL.searchParams.append("folder", folder);
    }
    return endpointURL.toString();
  };

  public static returnUploadPartQuery = (
    containerName: string,
    fileName: string,
    fileSize: number,
    filePartSize: number,
    filePartNumber: number,
    folder?: string
  ): string => {
    const endpointURL = new URL(
      `${ENV.apiUrl}/api/v1/uploadPart/${encodeURIComponent(
        containerName
      )}/file/${encodeURIComponent(
        fileName
      )}/ts/${fileSize}/ps/${filePartSize}/pn/${filePartNumber}`
    );
    if (folder) {
      endpointURL.searchParams.append("folder", folder);
    }
    return endpointURL.toString();
  };

  public static returnUploadPartsCommitQuery = (
    containerName: string,
    fileName: string,
    folder?: string
  ): string => {
    const endpointURL = new URL(
      `${ENV.apiUrl}/api/v1/uploadPartsCommit/${encodeURIComponent(
        containerName
      )}/file/${encodeURIComponent(fileName)}`
    );
    if (folder) {
      endpointURL.searchParams.append("folder", folder);
    }
    return endpointURL.toString();
  };

  public static returnGetFoldersQuery = (
    containerName: string,
    offset: number,
    limit: number,
    parentFolder?: string,
    folderPattern?: string
  ): string => {
    let endpointUrl = new URL(
      `${ENV.apiUrl}/api/v1/container/${encodeURIComponent(
        containerName
      )}/folders`
    );
    if (parentFolder) {
      endpointUrl.searchParams.append("folder", parentFolder);
    }
    if (folderPattern) {
      endpointUrl.searchParams.append("searchPattern", folderPattern);
    }
    endpointUrl.searchParams.append("Offset", offset.toString());
    endpointUrl.searchParams.append("Limit", limit.toString());
    return endpointUrl.toString();
  };

  public static returnMetadataUpdateQuery = (
    containerName: string,
    file: File
  ): string => {
    let endpointUrl = new URL(
      `${ENV.apiUrl}/api/v1/upload/${encodeURIComponent(
        containerName
      )}/metadata/fileObj`
    );
    endpointUrl.searchParams.append("fileName", file.name);
    endpointUrl.searchParams.append("folder", file.folderName);
    return endpointUrl.toString();
  };

  public static returnKeysFromContainerQuery = (
    containerName: string
  ): string => {
    let endpointURL = new URL(
      `${ENV.apiUrl}/api/v1/container/${encodeURIComponent(
        containerName
      )}/metadatakeys`
    );
    return endpointURL.toString();
  };

  public static returnMassMetadataUpdateQuery = (
    containerName: string
  ): string => {
    let endpointURL = new URL(
      `${ENV.apiUrl}/api/v1/upload/${encodeURIComponent(
        containerName
      )}/metadata/bulkUpdate`
    );
    return endpointURL.toString();
  };

  public static returnDownloadOnePartQuery = (
    containerName: string,
    fileName: string,
    fileGuid: string,
    startAt: number,
    totalBytes: number,
    folderName?: string
  ): string => {
    let endpointUrl = new URL(
      `${ENV.apiUrl}/api/v1/downloadPart/${encodeURIComponent(
        containerName
      )}/file/${encodeURIComponent(fileName)}/sa/${startAt}/ts/${totalBytes}`
    );
    endpointUrl.searchParams.append("fileGuid", fileGuid);
    if (folderName) {
      endpointUrl.searchParams.append("folder", folderName);
    }
    return endpointUrl.toString();
  };
}
